import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Skeleton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { IncomeByExperienceSkeleton } from "./IncomeByExperienceSkeleton";
import JobSuggestionSkeleton from "../common/JobSuggestionSkeleton";
import { AiToolsSkeleton } from "../AiToolsSection";
const getRandomWidth = () => Math.floor(Math.random() * 100);

export const SuggestedRolesSkeleton = () => (
  <Grid item xs={12} sm={12} lg={4} xl={4}>
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "39px",
        background: "#f5f5f5",
        minHeight: 250,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontFamily: "poppins",
          fontWeight: 700,
          textAlign: "center",
          mb: 2,
        }}
      >
        <Skeleton width="60%" height={32} sx={{ mx: "auto" }} />
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Skeleton width="80%" height={28} />
        </Grid>
        <Grid item xs={6}>
          <Skeleton width="80%" height={28} />
        </Grid>
        {[...Array(3)].map((_, idx) => (
          <React.Fragment key={idx}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton width="60%" height={24} sx={{ ml: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="60%" height={24} />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  </Grid>
);

// Skeleton for Other Roles with Responsibilities section
export const OtherRolesSkeleton = () => (
  <Grid item xs={12} sm={12} lg={4} xl={4}>
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "39px",
        background: "#f5f5f5",
        minHeight: 250,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontFamily: "poppins",
          fontWeight: 700,
          textAlign: "center",
          mb: 2,
        }}
      >
        <Skeleton width="70%" height={32} sx={{ mx: "auto" }} />
      </Typography>
      {[...Array(3)].map((_, idx) => (
        <Accordion
          key={idx}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            mb: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<Skeleton variant="circular" width={24} height={24} />}
          >
            <Skeleton width="60%" height={24} />
          </AccordionSummary>
          <AccordionDetails>
            <Skeleton width="90%" height={18} />
            <Skeleton width="80%" height={18} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  </Grid>
);

export const CoursesSkeleton = () => (
  <Grid item xs={12} sm={12} lg={4} xl={4}>
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "32px",
        background: "#f5f5f5",
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontWeight: 700,
          textAlign: "center",
          mb: 3,
        }}
      >
        <Skeleton width={180} height={32} sx={{ mx: "auto" }} />
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {[...Array(4)].map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rectangular"
            height={44}
            sx={{
              borderRadius: "12px",
              width: "100%",
              maxWidth: 320,
              mx: "auto",
            }}
          />
        ))}
      </Box>
    </Box>
  </Grid>
);

// Skeleton for Career Path sectio
export const HigherRolesSkeleton = () => (
  <Grid item xs={12}>
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "39px",
        background: "#f5f5f5", // updated light blue background like in image
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: 400,
        border: "1px solid #d6e4ff",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontFamily: "poppins",
          fontWeight: 700,
          textAlign: "center",
          mb: 4,
        }}
      >
        <Skeleton width={280} height={32} sx={{ mx: "auto" }} />
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          width: "100%",
          maxWidth: 400,
        }}
      >
        {[...Array(4)].map((_, idx) => (
          <React.Fragment key={idx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                p: 2,
                width: "100%",
                borderRadius: 2,
                boxShadow: 1,
                backgroundColor: "#fff",
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton height={24} width="80%" />
                <Skeleton height={18} width="60%" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
            {idx < 6 && (
              <Box
                sx={{
                  width: 2,
                  height: 20,
                  backgroundColor: "#e0e0e0",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  </Grid>
);

const InsightsSkeleton = () => {
  return (
    <Grid
      container
      spacing={{ xs: 0, md: 2 }}
      gap={{ xs: 2, md: 0 }}
      sx={{
        p: { xs: "20px 10px", md: "46px 34px 46px 20px" },
        backgroundColor: "#f9f9f9",
        borderRadius: { xs: "20px", md: "66px" },
        // ml: { xs: 1 },
      }}
    >
      {/* Header Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            style={{ fontWeight: "bold" }}
          >
            <Skeleton variant="circular" width={50} height={50} />
          </Typography>
          <Typography
            variant="h4"
            align="center"
            style={{ fontWeight: "bold", marginLeft: "20px" }}
          >
            <Skeleton width={200} height={"80px"} />
          </Typography>
        </Box>
      </Grid>

      {/* Subskills Section */}
      <Grid item xs={12} md={3}>
        <Card
          sx={{
            borderRadius: "39px",
            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              <Skeleton
                width="60%"
                style={{
                  margin: "5px auto",
                }}
                height={50}
              />
            </Typography>
            <Grid
              container
              spacing={1}
              alignItems={"center"}
              justifyContent={"center"}
              style={{ marginTop: "10px" }}
            >
              {[...Array(18)].map((_, index) => (
                <Grid item key={index}>
                  <Skeleton
                    variant="rectangular"
                    width={110}
                    // width={0}
                    height={30}
                    style={{ borderRadius: "20px" }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Enterprise Adoption and Popularity */}
      <Grid item xs={12} md={3}>
        <Card
          sx={{
            borderRadius: "39px",
            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          }}
        >
          <CardContent
            style={{
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              <Skeleton
                width={"60%"}
                style={{
                  margin: "5px auto",
                }}
                height={50}
              />
            </Typography>

            <Skeleton
              variant="circular"
              width={100}
              height={100}
              style={{ margin: "20px auto" }}
            />

            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                // marginTop: "20px",
                margin: "5px auto",
                textAlign: "center",
              }}
            >
              <Skeleton
                width="60%"
                height={50}
                style={{ margin: "5px auto" }}
              />
            </Typography>

            <Skeleton
              variant="circular"
              width={100}
              height={100}
              style={{ margin: "20px auto" }}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Latest Technologies */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            borderRadius: "39px",

            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              <Skeleton
                width="60%"
                style={{
                  margin: "5px auto",
                }}
                height={50}
              />
            </Typography>
            <table style={{ width: "100%", marginTop: "10px" }}>
              <thead>
                <tr>
                  <th>
                    <Skeleton width="80%" height={30} />
                  </th>
                  <th>
                    <Skeleton width="80%" height={30} />
                  </th>
                  <th>
                    <Skeleton width="80%" height={30} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td>
                      <Skeleton width="80%" height={35} />
                    </td>
                    <td>
                      <Skeleton width="85%" height={35} />
                    </td>
                    <td>
                      <Skeleton width="65%" height={35} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </Grid>


      {/* AI Tools Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: "39px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "poppins",
              fontWeight: 700,
              textAlign: "center",
              mb: 4,
            }}
          >
            <Skeleton width={280} height={32} sx={{ mx: "auto" }} />
          </Typography>
          <AiToolsSkeleton />
        </Box>
      </Grid>

      {/* Suggested Roles Section */}
      <SuggestedRolesSkeleton />
      {/* Other Roles with Responsibilities Section */}
      <OtherRolesSkeleton />
      <CoursesSkeleton />
      {/* Career Path Section */}
      <HigherRolesSkeleton />

      <IncomeByExperienceSkeleton />

      {/* <JobSuggestionSkeleton /> */}

      {/* Interview Questions */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            borderRadius: "39px",
            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              <Skeleton
                width="60%"
                height={50}
                style={{
                  margin: "5px auto",
                }}
              />
            </Typography>
            {[...Array(6)].map((_, index) => (
              <Typography
                variant="body2"
                style={{ marginTop: "10px" }}
                key={index}
              >
                <Skeleton width="100%" animation="wave" />
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Game-Changers Section */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            borderRadius: "39px",
            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              <Skeleton
                width="60%"
                height={50}
                style={{
                  margin: "5px auto",
                }}
              />
            </Typography>
            {[...Array(6)].map((_, index) => (
              <Typography
                variant="body2"
                style={{ marginTop: "10px" }}
                key={index}
              >
                <Skeleton width="100%" animation="wave" />
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default InsightsSkeleton;
